import AppDemo1 from "@/assets/demo-1.webp";
import AppDemo2 from "@/assets/demo-2.webp";
import AppDemo3 from "@/assets/demo-3.webp";
import {
  FacebookIcon,
  InstagramIcon,
  RedditIcon,
  YoutubeIcon,
} from "@/assets/icons/svg-icons";
import type { InputCardOption } from "@/components/inputs/types";
import type { IFeatures } from "@/lib/types";
import {
  Anchor,
  Clock3,
  Home,
  Moon,
  ShieldCheck,
  Sparkle,
  UserRoundPen,
  type LucideIcon,
} from "lucide-react";

export const FEATURES: IFeatures[] = [
  {
    eyebrow: "• Trust made visible",
    heading: "Vouched by people who know them",
    description:
      "Friends, family, and those closest to someone can speak to their character in their own words. Written openly and signed by name, so trust is something you can actually read.",
    items: [
      "Written by people who truly know them",
      "Signed with real names, never anonymous",
    ],
    image: AppDemo1,
    imagePosition: "right",
  },
  {
    eyebrow: "• Presence over photos",
    heading: "Meet the person, not just the photo",
    description:
      "The home feed is made of posts. Thoughts, interests, and perspective sit front and center, so a photo is one part of someone, never the whole of them.",
    items: [
      "A feed of who someone actually is",
      "Values and intentions, stated openly",
    ],
    image: AppDemo2,
    imagePosition: "left",
  },
  {
    eyebrow: "• Intention, not volume",
    heading: "Connect with intention",
    description:
      "Every connection here is a considered choice, made at a pace that respects how serious this decision really is. You reach out to someone because you mean it.",
    items: [
      "Requests made with thought, not reflex",
      "A pace that respects a real decision",
    ],
    image: AppDemo3,
    imagePosition: "right",
  },
];

// These are going to be routes
export const FOOTER_NAV_LINKS = [
  {
    label: "Terms and conditions",
    link: "terms-and-conditions",
  },
  {
    label: "Privacy policy",
    link: "privacy-policy",
  },
  {
    label: "Contact us",
    link: "contact-us",
  },

  // {
  //   label: "Contact us",
  //   link: "https://www.reddit.com/r/sakeena_io/",
  //   Icon: FaReddit,
  // },
];

export const FOOTER_LINKS = [
  {
    label: "Facebook",
    link: "https://www.facebook.com/people/Sakeena/61556705273894",
    Icon: FacebookIcon,
  },
  {
    label: "Instagram",
    link: "https://www.instagram.com/sakeena.io/",
    Icon: InstagramIcon,
  },
  {
    label: "YouTube",
    link: "https://www.youtube.com/@Sakeenaio",
    Icon: YoutubeIcon,
  },
  {
    label: "Reddit",
    link: "https://www.reddit.com/r/SakeenaMuslimMarriage/",
    Icon: RedditIcon,
  },
];

export const getBenefits = (
  // name: string,
  gender: string,
): { icon: LucideIcon; title: string; description: string }[] => {
  const isMale = gender?.toLowerCase() === "male";

  return [
    {
      icon: ShieldCheck,
      title: `You know ${isMale ? "him" : "her"} in a way a profile can't show`,
      description: `That's the part you're being asked for.`,
    },
    {
      icon: UserRoundPen,
      title: `This goes on ${isMale ? "his" : "her"} profile`,
      description: `On Sakeena, in your words, under your name.`,
    },
    {
      icon: Clock3,
      title: `Takes under 2 minutes`,
      description: `A few short questions.`,
    },
  ];
};

export const PHONE_REGEX = /^\+\d+$/;
// eslint-disable-next-line no-useless-escape
export const EMAIL_REGEX = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

export const INVALID_CHARS= /^[.\d\s!@#$%^&*()_+=\-\\|;:'",<>?/`~]+$/;

export	const NAME_PATTERN = /^[A-Za-z][A-Za-z' -]{1,49}$/;

export const REPEATED_CHAR_PATTERN = /(.)\1\1/i;

export const PHONE_WHITESPACE_PATTERN = /\s/;

export const PHONE_SEPARATOR_PATTERN = /[^+\d]/;


export const MIN_VOUCH_LETTERS = 100;

// Counts letters in any script; marks (\p{M}) are included so Hindi/Urdu vowel signs aren't dropped,
// except emoji variation selectors and the keycap mark (e.g. "❤️", "1️⃣"), which are marks too
export const countVouchLetters = (text: string) =>
	text.match(/(?![︎️⃣])[\p{L}\p{M}]/gu)?.length ?? 0;

export const SAMPLE_VOUCH_ID = "VCH-1738744e-99a6-498e-8ac1-53f8b8ce4c03";

// Vouch form options

export const CONFIRMATION_OPTIONS: InputCardOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export const RECOMMENDATION_OPTIONS: InputCardOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export const RELATIONSHIP_OPTIONS = [
  {
    value: "parent",
    label: "Parent",
  },
  {
    value: "sibling",
    label: "Sibling",
  },
  {
    value: "cousin",
    label: "Cousin",
  },
  {
    value: "family",
    label: "Family",
  },
  {
    value: "friend",
    label: "Friend",
  },
  {
    value: "colleague",
    label: "Colleague",
  },
];

export const DURATION_OPTIONS: InputCardOption[] = [
  { value: "less_than_1_year", label: "Less than 1 year" },
  { value: "1_to_3_years", label: "1 to 3 years" },
  { value: "3_to_5_years", label: "3 to 5 years" },
  { value: "more_than_5_years", label: "More than 5 years" },
];

export const COMMENTS_OPTIONS: InputCardOption[] = [
  { value: "genuine", label: "Genuine and straightforward", icon: Sparkle },
  { value: "caring", label: "Caring and family-oriented", icon: Home },
  { value: "mature", label: "Mature and grounded", icon: Anchor },
  { value: "serious", label: "Serious about their deen", icon: Moon },
];

export const MARITAL_STATUSES_OPTIONS: InputCardOption[] = [
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" },
];

export const DOWNLOAD_LINK = {
  APP_STORE_LINK:
    "https://apps.apple.com/ca/app/sakeena-halal-muslim-marriage/id6593691962",
  PLAY_STORE_LINK:
    "https://play.google.com/store/apps/details?id=com.kodelle.sakeena&hl=en",
};
